import React, { useState, useEffect } from 'react';
import { Typography, Card, List, Button, Spin, Empty, message, Space, Tag, Rate } from 'antd';
import { HeartFilled, ShoppingCartOutlined, EyeOutlined } from '@ant-design/icons';
import { wishlistService, cartService, storageUrl } from '../services/api';
import BookingModal from '../components/BookingModal';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;

const Wishlist = () => {
  const [loading, setLoading] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [updating, setUpdating] = useState({});
  const [bookingProduct, setBookingProduct] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const response = await wishlistService.getWishlist();
      const items = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setWishlist(items);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (wishlistId) => {
    setUpdating((prev) => ({ ...prev, [wishlistId]: true }));
    try {
      await wishlistService.removeFromWishlist(wishlistId);
      message.success('Item removed from wishlist');
      fetchWishlist();
    } catch (error) {
      message.error('Failed to remove item from wishlist');
    } finally {
      setUpdating((prev) => ({ ...prev, [wishlistId]: false }));
    }
  };

  const handleAddToCart = async (wishlistItem) => {
    const productId = wishlistItem.product_service?.id || wishlistItem.product_service_id;
    const productType = wishlistItem.product_service?.type;
    if (productType === 'service') {
      setBookingProduct(wishlistItem.product_service);
      return;
    }
    setUpdating((prev) => ({ ...prev, [`cart-${productId}`]: true }));
    try {
      await cartService.addToCart(productId);
      message.success('Item added to cart');
    } catch (error) {
      message.error('Failed to add item to cart');
    } finally {
      setUpdating((prev) => ({ ...prev, [`cart-${productId}`]: false }));
    }
  };

  const handleServiceBooking = async (bookingData) => {
    if (!bookingProduct) return;
    setBookingLoading(true);
    try {
      await cartService.addToCart(bookingProduct.id, 1, bookingData);
      message.success(`${bookingProduct.name} booked! Check your cart when ready.`);
      setBookingProduct(null);
    } catch (error) {
      message.error('Failed to book service');
    } finally {
      setBookingLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price || 0);
  };

  const handleViewProduct = (productId) => {
    navigate(`/customer/products/${productId}`);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="customer-page" style={{ padding: '0' }}>
      <div className="customer-page-header">
        <Title level={2}>My Wishlist</Title>
        <Paragraph>Keep a list of products you want to save and buy later.</Paragraph>
      </div>

      {wishlist.length === 0 ? (
        <Card className="customer-card">
          <Empty description="Your wishlist is empty" image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button className="customer-btn-primary" onClick={() => navigate('/customer/products')}>
              Browse Products
            </Button>
          </Empty>
        </Card>
      ) : (
        <List
          split={false}
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 4 }}
          dataSource={wishlist}
          renderItem={(item, idx) => (
            <List.Item>
              <Card
                className="customer-card animate-fade-slide-up"
                style={{ animationDelay: `${idx * 0.05}s`, opacity: 0 }}
                cover={
                  <div
                    className="customer-list-img"
                    style={{
                      height: 200,
                      background: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={
                        storageUrl(
                          item.product_service?.images?.[0]?.url ||
                            item.product_service?.images?.[0]?.image_url,
                        ) ||
                        `https://placehold.co/200x200?text=${encodeURIComponent(item.product_service?.name || 'Product')}`
                      }
                      alt={item.product_service?.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Button
                      type="text"
                      shape="circle"
                      icon={<HeartFilled />}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'white',
                        color: '#e5484d',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      }}
                      onClick={() => handleRemoveFromWishlist(item.id)}
                      loading={updating[item.id]}
                    />
                  </div>
                }
              >
                <Card.Meta
                  title={
                    <Text strong style={{ fontSize: 16, color: '#2d5a3d' }}>
                      {item.product_service?.name || 'Product'}
                    </Text>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.product_service?.category || 'General'}
                        </Text>
                      </div>

                      {item.product_service?.rating && (
                        <div style={{ marginBottom: 8 }}>
                          <Rate
                            disabled
                            defaultValue={parseFloat(item.product_service.rating)}
                            style={{ fontSize: 12 }}
                          />
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                            ({item.product_service.reviews || 0})
                          </Text>
                        </div>
                      )}

                      <div style={{ marginBottom: 8 }}>
                        <Text strong style={{ fontSize: 18, color: '#2d5a3d' }}>
                          {formatPrice(
                            item.product_service?.sale_price ?? item.product_service?.price,
                          )}
                        </Text>
                        {item.product_service?.sale_price != null &&
                          Number(item.product_service.sale_price) <
                            Number(item.product_service.price) && (
                            <Text delete type="secondary" style={{ marginLeft: 8 }}>
                              {formatPrice(item.product_service.price)}
                            </Text>
                          )}
                      </div>

                      <div>
                        <Tag
                          className={item.product_service?.is_available ? 'customer-tag-green' : ''}
                          color={item.product_service?.is_available ? undefined : 'error'}
                        >
                          {item.product_service?.is_available ? 'In Stock' : 'Out of Stock'}
                        </Tag>
                        {item.product_service?.type === 'service' && (
                          <Tag color="blue">Service</Tag>
                        )}
                      </div>

                      {item.product_service?.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.product_service.description.substring(0, 80)}
                          {item.product_service.description.length > 80 ? '...' : ''}
                        </Text>
                      )}
                    </div>
                  }
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <Button
                    className="customer-btn-primary"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => handleAddToCart(item)}
                    loading={updating[`cart-${item.product_service_id}`]}
                    style={{ border: 'none', flex: 1 }}
                  >
                    Add to Cart
                  </Button>
                  <Button
                    className="customer-btn-secondary"
                    icon={<EyeOutlined />}
                    onClick={() =>
                      handleViewProduct(item.product_service?.id || item.product_service_id)
                    }
                    style={{ flex: 1 }}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      {wishlist.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 32, marginBottom: 24 }}>
          <Space>
            <Button
              className="customer-btn-primary"
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/cart')}
              size="large"
            >
              View Cart
            </Button>
            <Button
              className="customer-btn-secondary"
              onClick={() => navigate('/customer/products')}
              size="large"
            >
              Continue Shopping
            </Button>
          </Space>
        </div>
      )}

      <BookingModal
        open={!!bookingProduct}
        product={bookingProduct}
        onCancel={() => setBookingProduct(null)}
        onConfirm={handleServiceBooking}
        loading={bookingLoading}
      />
    </div>
  );
};

export default Wishlist;
